import { LoadingPage } from "src/components/loading/page";

import { useRouter } from "next/router";
import { useContext, useMemo } from "react";
import { SessionContext } from "src/components/session/session";
import { useActiveContext } from "shared/ContextSwitcher/store";
import { LoggedInContextProvider } from "./LoggedInContext";

import { isServerSide } from "src/utils/env/env";
import { useLoginUrl } from "src/hooks/useLoginUrl";

import type { SessionWithUser } from "src/utils/users/types";

export function behindLoggedInContext<Props extends {}>(
  Page: React.FC<Props>
): React.FC<Props> {
  return function PageBehindLoggedInContext(props: Props) {
    const router = useRouter();
    const loginUrl = useLoginUrl();
    const { session, sessionLoading } = useContext(SessionContext);
    const activeContext = useActiveContext();

    const sessionWithUser: SessionWithUser | undefined = useMemo(() => {
      if (session?.user) {
        return { ...session, user: session.user };
      } else {
        return undefined;
      }
    }, [session]);

    if (sessionLoading || isServerSide() || !router.isReady) {
      // Loading
      return <LoadingPage />;
    }

    if (!sessionWithUser) {
      // Logged out
      router.replace(loginUrl);

      // (Show loading page until we get redirected)
      return <LoadingPage />;
    }

    if (!activeContext) {
      // Logged in, but still loading activeContext
      return <LoadingPage />;
    }

    // Logged in
    return (
      <LoggedInContextProvider
        value={{
          activeContext,
          session: sessionWithUser,
        }}
      >
        <Page {...props} />
      </LoggedInContextProvider>
    );
  };
}
